"use client";

import {
  EyeOff,
  Calendar,
  Clock,
  Mail,
  Image as ImageIcon,
  Link as LinkIcon,
  Plus,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { notFound } from "next/navigation";
import { useState, useEffect, use } from "react";

// Imported per-module rather than through the "@/components/common" barrel:
// that barrel also re-exports RichTextEditor, which drags CodeMirror (vim.js,
// sqf.js, purify, token.js) into this page bundle for nothing.
import { Header } from "@/components/common/Header";
import { Footer } from "@/components/common/Footer";
import BlogSkeleton from "@/components/common/BlogSkeleton";
import blogCacheManager from "@/lib/blogCache";
import { authorName } from "@/lib/authors";

// The blog generator prepends the featured image into the body as a leading
// block (e.g. <div style="text-align:center"><img ...></div>) which duplicates
// the "Featured Image" rendered above the content. When the post still uses a
// placeholder it shows up as a broken/empty image. Strip that leading image so
// it never renders — only when it's a placeholder or a duplicate of the
// thumbnail, so genuine in-article images are left untouched.
const LEADING_IMAGE_BLOCKS = [
  /^<div[^>]*>\s*<img\b[^>]*>\s*<\/div>/i, // <div ...><img></div>
  /^<p>\s*<img\b[^>]*>\s*<\/p>/i, // <p><img></p>
  /^<p>\s*!\s*<a\b[^>]*>[\s\S]*?<\/a>\s*<\/p>/i, // malformed markdown image -> <p>!<a>..</a></p>
  /^<img\b[^>]*>/i, // bare leading <img>
];

const isRemovableLeadImage = (url, heroSrc) =>
  !!url && (url.includes("picsum") || (heroSrc && url === heroSrc));

const stripLeadingFeaturedImage = (html, heroSrc) => {
  if (!html) return html;
  const trimmed = html.replace(/^\s+/, "");
  for (const re of LEADING_IMAGE_BLOCKS) {
    const match = trimmed.match(re);
    if (!match) continue;
    const src = match[0].match(/src=["']([^"']+)["']/i)?.[1];
    const href = match[0].match(/href=["']([^"']+)["']/i)?.[1];
    if (isRemovableLeadImage(src || href, heroSrc)) {
      return trimmed.slice(match[0].length).replace(/^\s+/, "");
    }
    break; // leading image is a real, distinct image — keep it
  }
  return trimmed;
};

const stripLeadingMarkdownImage = (markdown, heroSrc) => {
  if (!markdown) return markdown;
  const match = markdown.replace(/^\s+/, "").match(/^!\[[^\]]*\]\(([^)]+)\)/);
  if (match && isRemovableLeadImage(match[1], heroSrc)) {
    return markdown.replace(/^\s*!\[[^\]]*\]\([^)]+\)\s*/, "");
  }
  return markdown;
};

// Ultra-optimized blog component with zero loading time.
// initialBlog is server-fetched in page.jsx so the full article body is in
// the server-rendered HTML (SEO/AI crawlers read it without executing JS).
const BlogPost = ({ params, initialBlog, initialRelated }) => {
  const [blog, setBlog] = useState(initialBlog || null);
  // Server-selected in page.jsx (see lib/relatedPosts.js). Seeding state here
  // is what puts these links in the server-rendered HTML — while this list was
  // filled in by the effect below, no crawler ever saw a single one of them.
  const [relatedBlogs, setRelatedBlogs] = useState(initialRelated || []);
  const [showSkeleton, setShowSkeleton] = useState(!initialBlog);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  // Unwrap params Promise for Next.js 15 compatibility
  const resolvedParams = use(params);

  // Initialize admin preview state immediately to avoid double fetching
  const [isAdminPreview] = useState(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get("admin_preview") === "true";
    }
    return false;
  });

  useEffect(() => {
    const initializeBlog = async () => {
      // Server already rendered the article from initialBlog, and page.jsx
      // already picked the recommendations — nothing left to fetch. Admin
      // preview still refetches below (it may need hidden data).
      if (
        initialBlog &&
        !isAdminPreview &&
        initialBlog.slug === resolvedParams.slug
      ) {
        return;
      }

      // First, check if we have data from blogs page (sessionStorage)
      if (typeof window !== "undefined") {
        const storedBlog = sessionStorage.getItem("currentBlog");

        if (storedBlog) {
          try {
            const parsedBlog = JSON.parse(storedBlog);

            // Check if this is the correct blog and data is recent (within 5 minutes)
            if (
              parsedBlog.slug === resolvedParams.slug &&
              Date.now() - parsedBlog.timestamp < 300000
            ) {
              // INSTANT LOAD - No loading time!
              setBlog(parsedBlog);
              setShowSkeleton(false);

              // Fetch related blogs in background
              setTimeout(async () => {
                try {
                  const relatedData =
                    await blogCacheManager.prefetchRelatedBlogs(
                      parsedBlog.id,
                      parsedBlog.tags
                    );
                  if (relatedData) {
                    setRelatedBlogs(relatedData.blogs);
                  }
                } catch (error) {
                  console.error("Related blogs fetch error:", error);
                }
              }, 100);

              return;
            }
          } catch (error) {
            console.error("Session storage parse error:", error);
          }
        }

        // If no sessionStorage data, try cache
        if (window.__BLOG_CACHE) {
          const cachedBlog = Object.values(window.__BLOG_CACHE).find(
            (b) => b.slug === resolvedParams.slug
          );

          if (cachedBlog) {
            setBlog(cachedBlog);
            setShowSkeleton(false);

            // Fetch related blogs
            setTimeout(async () => {
              try {
                const relatedData = await blogCacheManager.prefetchRelatedBlogs(
                  cachedBlog.id,
                  cachedBlog.tags
                );
                if (relatedData) {
                  setRelatedBlogs(relatedData.blogs);
                }
              } catch (error) {
                console.error("Related blogs fetch error:", error);
              }
            }, 100);
            return; // Exit if found in cache
          }
        }
      }

      // Last resort: fetch from API (works for both client and server side)
      try {
        const response = await fetch(
          `/api/blogs/slug/${resolvedParams.slug}${
            isAdminPreview ? "?admin_preview=true" : ""
          }`
        );
        const data = await response.json();

        if (data.success && data.data) {
          setBlog(data.data);
          setShowSkeleton(false);

          // Fetch related blogs
          const relatedData = await blogCacheManager.prefetchRelatedBlogs(
            data.data.id,
            data.data.tags
          );
          if (relatedData) {
            setRelatedBlogs(relatedData.blogs);
          }
        } else {
          console.error("Blog not found:", data.error);
          notFound();
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        notFound();
      }
    };

    initializeBlog();
  }, [resolvedParams.slug, isAdminPreview, initialBlog]);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const formatReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = (content || "").split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return minutes;
  };

  // Show skeleton while loading
  if (showSkeleton) {
    return (
      <>
        <Header />
        <BlogSkeleton />
        <Footer />
      </>
    );
  }

  if (!blog) {
    notFound();
  }

  return (
    <div className="min-h-screen bg-[#212121]">
      <Header />

      {/* The root layout hard-codes <html lang="en">, so without this every
          translated post declared itself English while its text was German or
          French. Google detects language from the text; Bing and Yandex also
          read the attribute. */}
      <main id="main" lang={blog.language === "en-GB" ? "en" : blog.language || "en"}>
      {/* Main Content Container */}
      <div className="container mx-auto px-4 pt-40 pb-24 max-w-6xl">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link href="/" className="text-[#A4A4A4] hover:text-white transition-colors">
              Home
            </Link>
            <span className="text-[#909090]">/</span>
            <Link
              href={isAdminPreview ? "/blogs?admin_preview=true" : "/blogs"}
              className="text-[#A4A4A4] hover:text-white transition-colors"
            >
              Blogs
            </Link>
            <span className="text-[#909090]">/</span>
            <span className="text-[#909090]">{blog.title}</span>
          </div>
        </nav>

        {/* Admin Preview Notice */}
        {isAdminPreview && blog.visibility === "hidden" && (
          <div className="mb-8 p-4 bg-red-500/10 border-2 border-red-500/30 rounded-xl">
            <div className="flex items-center text-red-400">
              <EyeOff className="w-5 h-5 mr-2" />
              <span className="font-bold text-sm uppercase tracking-wider">
                Hidden Post - Admin Preview Mode
              </span>
            </div>
            <p className="text-red-400/80 text-sm mt-2">
              This post is currently hidden from public view and only visible in
              admin preview mode.
            </p>
          </div>
        )}

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {blog.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/70 border border-white/10"
              >
                <span className="mr-1">#</span>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="text-3xl lg:text-4xl font-medium tracking-tight mb-6 leading-tight text-[#F8F7F4]">
          {blog.title}
        </h1>

        {/* Excerpt */}
        {blog.excerpt && (
          <p className="text-lg leading-relaxed mb-8 text-[#A4A4A4]">
            {blog.excerpt}
          </p>
        )}

        {/* Author and meta info after excerpt */}
        <div className="flex justify-between items-center mb-8 pt-4 border-t border-white/10">
          <div className="flex items-center space-x-4">
            <div>
              <p className="font-semibold text-[#F8F7F4]">
                By {authorName(blog.author?.username) || "Keystone Team"}
              </p>
            </div>
            <div className="flex items-center text-sm text-[#909090]">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{formatDate(blog.createdAt)}</span>
            </div>
            <div className="flex items-center text-sm text-[#909090]">
              <Clock className="w-4 h-4 mr-1" />
              <span>{formatReadingTime(blog.content || "")} min read</span>
            </div>
          </div>

          {/* Share buttons */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-[#909090] mr-3 font-medium">
              Share:
            </span>
            {/* Facebook */}
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                typeof window !== "undefined" ? window.location.href : ""
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative p-2.5 rounded-full bg-white/5 hover:bg-[#1877F2] text-[#A4A4A4] hover:text-white transition-all duration-300 transform hover:scale-105"
              title="Share on Facebook"
            >
              <svg
                className="w-4 h-4"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#1E1E1E] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Facebook
              </div>
            </a>
            {/* Twitter/X */}
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                typeof window !== "undefined" ? window.location.href : ""
              )}&text=${encodeURIComponent(blog.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative p-2.5 rounded-full bg-white/5 hover:bg-white text-[#A4A4A4] hover:text-black transition-all duration-300 transform hover:scale-105"
              title="Share on X (Twitter)"
            >
              <svg
                className="w-4 h-4"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#1E1E1E] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Twitter
              </div>
            </a>
            {/* LinkedIn */}
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                typeof window !== "undefined" ? window.location.href : ""
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative p-2.5 rounded-full bg-white/5 hover:bg-[#0A66C2] text-[#A4A4A4] hover:text-white transition-all duration-300 transform hover:scale-105"
              title="Share on LinkedIn"
            >
              <svg
                className="w-4 h-4"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#1E1E1E] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                LinkedIn
              </div>
            </a>
            {/* Email */}
            <a
              href={`mailto:?subject=${encodeURIComponent(
                blog.title
              )}&body=${encodeURIComponent(
                `Check out this article: ${blog.title}\n\n${
                  typeof window !== "undefined" ? window.location.href : ""
                }`
              )}`}
              className="group relative p-2.5 rounded-full bg-white/5 hover:bg-green-500 text-[#A4A4A4] hover:text-white transition-all duration-300 transform hover:scale-105"
              title="Share via Email"
            >
              <Mail className="w-4 h-4" />
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#1E1E1E] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Email
              </div>
            </a>
            {/* Copy Link */}
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: blog.title,
                    url: window.location.href,
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  // You could add a toast notification here
                }
              }}
              className="group relative p-2.5 rounded-full bg-white/5 hover:bg-white/20 text-[#A4A4A4] hover:text-white transition-all duration-300 transform hover:scale-105"
              title="Copy Link"
            >
              <LinkIcon className="w-4 h-4" />
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#1E1E1E] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Copy Link
              </div>
            </button>
          </div>
        </div>

        {/* Featured Image */}
        {blog.thumbnailImage && (
          <div className="mb-12">
            <div className="relative">
              <Image
                src={blog.thumbnailImage}
                alt={blog.title || "Blog featured image"}
                width={800}
                height={500}
                className="w-full h-auto rounded-lg"
                priority
                // Without this the preload Next emits carries no priority hint,
                // which is the one thing Lighthouse's LCP-discovery check looks
                // for; `priority` alone only marks the <img>.
                fetchPriority="high"
                onError={(e) => {
                  console.error("Image failed to load:", blog.thumbnailImage);
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>
        )}

        {/* Content with custom styling */}
        <div
          className="section-block-padding blog-posts blog-content"
          style={{ minHeight: "400px" }}
        >
          {/* Debug info */}
          {typeof window !== "undefined" &&
            window.location.search.includes("debug=true") && (
              <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
                <h4 className="font-bold text-sm mb-2 text-yellow-200">DEBUG INFO:</h4>
                <div className="text-xs space-y-2 text-yellow-200/80">
                  <div>
                    <strong>blog.htmlContent exists:</strong>{" "}
                    {blog.htmlContent ? "YES" : "NO"}
                  </div>
                  <div>
                    <strong>blog.content exists:</strong>{" "}
                    {blog.content ? "YES" : "NO"}
                  </div>
                  <div>
                    <strong>blog.thumbnailImage:</strong>{" "}
                    {blog.thumbnailImage || "NOT SET"}
                  </div>
                  <div>
                    <strong>Image Source Used:</strong>{" "}
                    {blog.thumbnailImage || "NO IMAGE"}
                  </div>
                  {blog.htmlContent && (
                    <div>
                      <strong>HTML Content:</strong>
                      <pre className="bg-[#1E1E1E] p-2 mt-1 text-xs max-h-32 overflow-auto">
                        {blog.htmlContent}
                      </pre>
                    </div>
                  )}
                  {blog.content && (
                    <div>
                      <strong>Markdown Content:</strong>
                      <pre className="bg-[#1E1E1E] p-2 mt-1 text-xs max-h-32 overflow-auto">
                        {blog.content}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

          {blog.htmlContent ? (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: stripLeadingFeaturedImage(
                  blog.htmlContent,
                  blog.thumbnailImage
                ),
              }}
            />
          ) : blog.content ? (
            <div className="text-[#A4A4A4]">
              <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                {stripLeadingMarkdownImage(blog.content, blog.thumbnailImage)}
              </pre>
            </div>
          ) : (
            <div className="text-[#909090]">
              <p>Content is loading...</p>
            </div>
          )}
        </div>
      </div>

      {/* FAQ Section */}
      {blog.faqs && blog.faqs.length > 0 && (
        <section className="bg-[#212121] pb-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="pt-12 border-t border-white/10">
              <h2 className="text-3xl lg:text-4xl font-medium tracking-tight text-[#F8F7F4] mb-10">
                Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-4">
                {blog.faqs.map((faq, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div
                      key={faq.id || `faq-${index}`}
                      className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden transition-colors duration-300 hover:bg-white/[0.07]"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaqIndex(isOpen ? null : index)
                        }
                        className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left"
                        aria-expanded={isOpen}
                      >
                        <span className="text-lg lg:text-xl font-medium text-[#F8F7F4] leading-snug pr-2">
                          {faq.question}
                        </span>
                        <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-white/5">
                          <Plus
                            className={`w-4 h-4 text-[#F8F7F4] transition-transform duration-300 ease-in-out ${
                              isOpen ? "rotate-45" : "rotate-0"
                            }`}
                          />
                        </span>
                      </button>
                      <div
                        className="grid transition-all duration-300 ease-in-out"
                        style={{
                          gridTemplateRows: isOpen ? "1fr" : "0fr",
                        }}
                      >
                        <div className="overflow-hidden">
                          <p className="px-6 pb-6 text-base text-[#A4A4A4] leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Recommended For You Section */}
      {relatedBlogs.length > 0 && (
        <section className="py-16 bg-[#212121]">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-medium tracking-tight text-[#F8F7F4]">
                Recommended for you
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedBlogs.map((relatedBlog) => (
                <article key={relatedBlog.id} className="group cursor-pointer">
                  <Link
                    href={`/blogs/${relatedBlog.slug}${
                      isAdminPreview ? "?admin_preview=true" : ""
                    }`}
                    onClick={() => {
                      // Store the clicked blog for instant loading
                      sessionStorage.setItem(
                        "currentBlog",
                        JSON.stringify({
                          ...relatedBlog,
                          timestamp: Date.now(),
                        })
                      );
                    }}
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden rounded-lg mb-4">
                      {relatedBlog.thumbnailImage ? (
                        <Image
                          src={relatedBlog.thumbnailImage}
                          alt={relatedBlog.title || "Related blog image"}
                          width={300}
                          height={240}
                          className="w-full h-[240px] object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            console.error(
                              "Related blog image failed to load:",
                              relatedBlog.thumbnailImage
                            );
                            // Replace with fallback
                            e.currentTarget.parentElement.innerHTML = `
                              <div class="w-full h-[240px] bg-[#2a2a2a] flex items-center justify-center">
                                <svg class="w-16 h-16 text-[#909090]" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                                </svg>
                              </div>
                            `;
                          }}
                        />
                      ) : (
                        <div className="w-full h-[240px] bg-[#2a2a2a] flex items-center justify-center">
                          <ImageIcon className="w-16 h-16 text-[#909090]" />
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-medium text-[#F8F7F4] group-hover:text-white transition-colors line-clamp-2">
                      {relatedBlog.title}
                    </h3>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      </main>

      <Footer />

      {/* Custom Blog Content Styles - Dark Theme */}
      <style jsx global>{`
        .section-block-padding.blog-posts h2 {
          font-size: 40px;
          color: #F8F7F4;
          line-height: 1.2;
          margin-top: 64px;
          margin-bottom: 24px;
          font-weight: 500;
        }

        .section-block-padding.blog-posts h2:first-child {
          margin-top: 0;
        }

        .section-block-padding.blog-posts h3 {
          font-size: 32px;
          color: #F8F7F4;
          line-height: 1.2;
          margin-top: 48px;
          margin-bottom: 20px;
          font-weight: 500;
        }

        .section-block-padding.blog-posts h3:first-child {
          margin-top: 0;
        }

        .section-block-padding.blog-posts h4 {
          font-size: 24px;
          color: #F8F7F4;
          line-height: 1.4;
          margin-top: 40px;
          margin-bottom: 16px;
          padding-left: 0;
          font-weight: 500;
        }

        .section-block-padding.blog-posts h4:first-child {
          margin-top: 0;
        }

        .section-block-padding.blog-posts p {
          font-size: 18px;
          color: #A4A4A4;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        .section-block-padding.blog-posts p > a {
          font-size: 18px;
          color: #26DBC3;
          line-height: 1.6;
          margin-bottom: 24px;
          text-decoration: underline;
        }

        .section-block-padding.blog-posts p > strong {
          font-size: 18px;
          color: #F8F7F4;
          line-height: 1.6;
          margin-bottom: 24px;
          font-weight: 700;
        }

        .section-block-padding.blog-posts figure {
          margin: 56px 0;
        }

        .section-block-padding.blog-posts figure > div > img {
          width: 60%;
          height: auto;
          margin: 20px auto;
        }

        .section-block-padding.blog-posts figure > div > iframe {
          width: 60%;
          height: 550px;
          margin: 20px auto;
        }

        .section-block-padding.blog-posts > blockquote {
          font-size: 40px;
          color: #F8F7F4;
          line-height: 1.2;
          font-weight: 300;
          text-align: center;
          margin: 48px auto;
          padding: 0 6%;
        }

        .section-block-padding.blog-posts > blockquote > a {
          font-size: 40px;
          color: #26DBC3;
          line-height: 1.2;
          font-weight: 300;
          text-decoration: underline;
        }

        .section-block-padding.blog-posts figure > figcaption > em {
          font-size: 16px;
          color: #A4A4A4;
          line-height: 1.6;
          font-weight: 600;
          display: flex;
          justify-content: center;
        }

        .section-block-padding.blog-posts figure > figcaption > a > em {
          font-size: 16px;
          color: #26DBC3;
          line-height: 1.6;
          text-decoration: underline;
          font-weight: 600;
          display: flex;
          justify-content: center;
        }

        .section-block-padding.blog-posts > figcaption {
          font-size: 16px;
          color: #A4A4A4;
          line-height: 1.6;
          font-weight: 600;
          display: flex;
          justify-content: center;
        }

        .section-block-padding.blog-posts a.no-underline {
          background-color: #26DBC3;
          color: #fff;
          text-align: center;
          letter-spacing: -0.03;
          border-radius: 8px;
          padding: 16px 32px;
          font-size: 18px;
          line-height: 1.2;
          margin: 35px auto;
          display: flex;
          width: fit-content;
          font-weight: 600;
        }

        .section-block-padding.blog-posts a.blog-cta.w-inline-block > div {
          background-color: #2a2a2a;
          color: #fff;
          text-align: center;
          letter-spacing: -0.03;
          border-radius: 8px;
          padding: 16px 32px;
          font-size: 18px;
          line-height: 1.2;
          margin: 35px auto;
          display: flex;
          width: fit-content;
          font-weight: 600;
        }

        .section-block-padding.blog-posts ul {
          list-style-type: disc;
          padding-left: 24px;
          margin-bottom: 24px;
        }

        .section-block-padding.blog-posts ul > li {
          content: inherit;
          font-weight: 500;
          padding-bottom: 5px;
          color: #A4A4A4;
        }

        .section-block-padding.blog-posts ul > li::marker {
          content: inherit;
          font-weight: 500;
          padding-bottom: 5px;
        }

        .section-block-padding.blog-posts ol {
          list-style-type: decimal;
          padding-left: 8%;
          margin-bottom: 24px;
        }

        .section-block-padding.blog-posts ol > li {
          content: inherit;
          font-weight: 500;
          padding-bottom: 5px;
          color: #A4A4A4;
        }

        .section-block-padding.blog-posts ol > li::marker {
          content: inherit;
          font-weight: 500;
          padding-bottom: 5px;
        }

        .section-block-padding > p > iframe {
          width: 100% !important;
          height: 600px;
          border-radius: 16px;
        }

        .blog-posts a {
          color: #26DBC3;
          text-decoration: underline;
        }

        /* Override for strong/em inside headings to prevent conflicts */
        .section-block-padding.blog-posts h2 strong,
        .section-block-padding.blog-posts h3 strong,
        .section-block-padding.blog-posts h4 strong {
          color: #F8F7F4 !important;
        }

        /* Tables */
        .section-block-padding.blog-posts table {
          width: 100%;
          margin: 32px 0;
          border-collapse: separate;
          border-spacing: 0;
          background-color: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          overflow: hidden;
          font-size: 16px;
          line-height: 1.6;
        }

        .section-block-padding.blog-posts thead {
          background-color: rgba(255, 255, 255, 0.05);
        }

        .section-block-padding.blog-posts th {
          color: #F8F7F4;
          font-weight: 600;
          text-align: left;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          vertical-align: top;
        }

        .section-block-padding.blog-posts td {
          color: #A4A4A4;
          padding: 14px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          vertical-align: top;
        }

        .section-block-padding.blog-posts tbody tr:last-child td {
          border-bottom: none;
        }

        .section-block-padding.blog-posts tbody tr:hover {
          background-color: rgba(255, 255, 255, 0.02);
        }

        .section-block-padding.blog-posts td > strong,
        .section-block-padding.blog-posts th > strong {
          color: #F8F7F4;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .section-block-padding.blog-posts table {
            display: block;
            overflow-x: auto;
            white-space: nowrap;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogPost;
